<template>
  <div>
    <!-- آمار -->
    <b-row class="mb-2">
      <b-col md="3" sm="6" class="mb-1">
        <b-card class="text-center" bg-variant="danger" text-variant="white">
          <h3>{{ stats.totalLogs || 0 }}</h3>
          <p class="mb-0">کل لاگ‌ها</p>
        </b-card>
      </b-col>
      <b-col md="3" sm="6" class="mb-1">
        <b-card class="text-center" bg-variant="warning" text-variant="white">
          <h3>{{ stats.unreviewedLogs || 0 }}</h3>
          <p class="mb-0">بررسی نشده</p>
        </b-card>
      </b-col>
      <b-col md="3" sm="6" class="mb-1">
        <b-card class="text-center" bg-variant="info" text-variant="white">
          <h3>{{ stats.todayLogs || 0 }}</h3>
          <p class="mb-0">امروز</p>
        </b-card>
      </b-col>
      <b-col md="3" sm="6" class="mb-1">
        <b-card class="text-center" bg-variant="success" text-variant="white">
          <h3>{{ (stats.totalLogs || 0) - (stats.unreviewedLogs || 0) }}</h3>
          <p class="mb-0">بررسی شده</p>
        </b-card>
      </b-col>
    </b-row>

    <!-- توضیحات صفحه -->
    <b-alert show variant="info" class="mb-2">
      <h4 class="alert-heading">
        <feather-icon icon="InfoIcon" class="mr-50" />
        سیستم گزارش خطای اپلیکیشن موبایل
      </h4>
      <div class="alert-body">
        <p class="mb-50">
          این صفحه برای مشاهده و مدیریت خطاهایی است که در اپلیکیشن موبایل کاربران رخ می‌دهد.
          هر زمان که اپلیکیشن با مشکل مواجه شود یا کرش کند، اطلاعات کامل خطا به صورت خودکار به این سیستم ارسال می‌شود.
        </p>
        <p class="mb-0">
          <strong>اطلاعات ثبت شده شامل:</strong>
          پیام خطا، نوع دستگاه، نسخه اندروید، نسخه اپلیکیشن، اطلاعات کاربر، وضعیت شبکه، سطح باتری و لاگ‌های کنسول می‌باشد.
          با بررسی این اطلاعات می‌توانید مشکلات اپلیکیشن را شناسایی و رفع کنید.
        </p>
      </div>
    </b-alert>

    <!-- کارت اصلی -->
    <b-card>
      <b-row class="mb-2">
        <b-col md="6">
          <h4>
            <feather-icon icon="AlertTriangleIcon" class="text-danger" />
            لاگ‌های خطای اپلیکیشن
          </h4>
        </b-col>
        <b-col md="6" class="text-right">
          <b-button variant="outline-primary" size="sm" class="mr-1" @click="loadData">
            <feather-icon icon="RefreshCwIcon" />
            بروزرسانی
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="confirmDeleteAll">
            <feather-icon icon="Trash2Icon" />
            حذف همه
          </b-button>
        </b-col>
      </b-row>

      <!-- فیلترها -->
      <b-row class="mb-2">
        <b-col md="3">
          <b-form-input
            v-model="search"
            placeholder="جستجو..."
            @input="onSearch"
          />
        </b-col>
        <b-col md="2">
          <b-form-select v-model="filterReviewed" @change="loadData">
            <option value="">همه</option>
            <option :value="false">بررسی نشده</option>
            <option :value="true">بررسی شده</option>
          </b-form-select>
        </b-col>
        <b-col md="2">
          <b-form-select v-model="filterVersion" @change="loadData">
            <option value="">همه نسخه‌ها</option>
            <option v-for="v in appVersions" :key="v._id" :value="v._id">
              {{ v._id }} ({{ v.count }})
            </option>
          </b-form-select>
        </b-col>
      </b-row>

      <!-- جدول -->
      <v-data-table
        :headers="headers"
        :items="logs"
        :search="search"
        :items-per-page="50"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        class="elevation-1"
        :mobile-breakpoint="0"
      >
        <!-- ستون پیام خطا -->
        <template v-slot:item.errorMessage="{ item }">
          <span
            class="text-danger cursor-pointer"
            @click="showDetail(item)"
            :title="item.errorMessage"
          >
            {{ truncate(item.errorMessage, 50) }}
          </span>
        </template>

        <!-- ستون اطلاعات دستگاه -->
        <template v-slot:item.deviceInfo="{ item }">
          <div class="small">
            <div><strong>{{ item.deviceManufacturer }}</strong> {{ item.deviceModel }}</div>
            <div class="text-muted">Android {{ item.deviceVersion }}</div>
          </div>
        </template>

        <!-- ستون نسخه اپ -->
        <template v-slot:item.appVersion="{ item }">
          <b-badge variant="primary">{{ item.appVersion || '-' }}</b-badge>
        </template>

        <!-- ستون کاربر -->
        <template v-slot:item.userInfo="{ item }">
          <div class="small">
            <div>{{ item.userName || '-' }}</div>
            <div class="text-muted" dir="ltr">{{ item.userMobile || '-' }}</div>
          </div>
        </template>

        <!-- ستون وضعیت -->
        <template v-slot:item.isReviewed="{ item }">
          <b-badge :variant="item.isReviewed ? 'success' : 'warning'">
            {{ item.isReviewed ? 'بررسی شده' : 'جدید' }}
          </b-badge>
        </template>

        <!-- ستون تاریخ -->
        <template v-slot:item.creationTime="{ item }">
          <div class="small">
            {{ formatDate(item.creationTime) }}
          </div>
        </template>

        <!-- ستون عملیات -->
        <template v-slot:item.actions="{ item }">
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon"
            @click="showDetail(item)"
            title="مشاهده جزئیات"
          >
            <feather-icon icon="EyeIcon" />
          </b-button>
          <b-button
            v-if="!item.isReviewed"
            variant="flat-success"
            size="sm"
            class="btn-icon"
            @click="markAsReviewed(item)"
            title="علامت بررسی شده"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          <b-button
            variant="flat-danger"
            size="sm"
            class="btn-icon"
            @click="deleteLog(item)"
            title="حذف"
          >
            <feather-icon icon="TrashIcon" />
          </b-button>
        </template>
      </v-data-table>
    </b-card>

    <!-- مودال جزئیات -->
    <b-modal
      id="modal-detail"
      title="جزئیات لاگ خطا"
      size="xl"
      ok-only
      ok-title="بستن"
      scrollable
    >
      <div v-if="selectedLog">
        <!-- اطلاعات خطا -->
        <b-card class="mb-2" border-variant="danger">
          <h5 class="text-danger">
            <feather-icon icon="AlertCircleIcon" />
            اطلاعات خطا
          </h5>
          <b-row>
            <b-col md="6">
              <p><strong>نوع خطا:</strong> {{ selectedLog.errorType || '-' }}</p>
            </b-col>
            <b-col md="6">
              <p><strong>منبع:</strong> {{ selectedLog.errorSource || '-' }}</p>
            </b-col>
            <b-col md="6">
              <p><strong>خط:</strong> {{ selectedLog.errorLine || '-' }}</p>
            </b-col>
            <b-col md="6">
              <p><strong>ستون:</strong> {{ selectedLog.errorColumn || '-' }}</p>
            </b-col>
          </b-row>
          <p><strong>پیام خطا:</strong></p>
          <b-alert show variant="danger">{{ selectedLog.errorMessage }}</b-alert>
          <p v-if="selectedLog.stackTrace"><strong>Stack Trace:</strong></p>
          <pre v-if="selectedLog.stackTrace" class="bg-dark text-light p-2 small" style="max-height: 200px; overflow: auto;">{{ selectedLog.stackTrace }}</pre>
        </b-card>

        <!-- اطلاعات دستگاه -->
        <b-card class="mb-2" border-variant="info">
          <h5 class="text-info">
            <feather-icon icon="SmartphoneIcon" />
            اطلاعات دستگاه
          </h5>
          <b-row>
            <b-col md="4">
              <p><strong>سازنده:</strong> {{ selectedLog.deviceManufacturer || '-' }}</p>
            </b-col>
            <b-col md="4">
              <p><strong>مدل:</strong> {{ selectedLog.deviceModel || '-' }}</p>
            </b-col>
            <b-col md="4">
              <p><strong>پلتفرم:</strong> {{ selectedLog.devicePlatform || '-' }}</p>
            </b-col>
            <b-col md="4">
              <p><strong>نسخه اندروید:</strong> {{ selectedLog.deviceVersion || '-' }}</p>
            </b-col>
            <b-col md="4">
              <p><strong>UUID:</strong> <small dir="ltr">{{ selectedLog.deviceUuid || '-' }}</small></p>
            </b-col>
            <b-col md="4">
              <p><strong>سریال:</strong> {{ selectedLog.deviceSerial || '-' }}</p>
            </b-col>
            <b-col md="4">
              <p><strong>شبیه‌ساز:</strong> {{ selectedLog.deviceIsVirtual ? 'بله' : 'خیر' }}</p>
            </b-col>
          </b-row>
        </b-card>

        <!-- اطلاعات صفحه نمایش -->
        <b-card class="mb-2" border-variant="secondary">
          <h5>
            <feather-icon icon="MonitorIcon" />
            صفحه نمایش و شبکه
          </h5>
          <b-row>
            <b-col md="3">
              <p><strong>عرض:</strong> {{ selectedLog.screenWidth || '-' }}px</p>
            </b-col>
            <b-col md="3">
              <p><strong>ارتفاع:</strong> {{ selectedLog.screenHeight || '-' }}px</p>
            </b-col>
            <b-col md="3">
              <p><strong>جهت:</strong> {{ selectedLog.screenOrientation || '-' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>نوع اتصال:</strong> {{ selectedLog.connectionType || '-' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>آنلاین:</strong> {{ selectedLog.isOnline ? 'بله' : 'خیر' }}</p>
            </b-col>
          </b-row>
        </b-card>

        <!-- اطلاعات باتری و حافظه -->
        <b-card class="mb-2" border-variant="warning">
          <h5 class="text-warning">
            <feather-icon icon="BatteryIcon" />
            باتری و حافظه
          </h5>
          <b-row>
            <b-col md="3">
              <p><strong>سطح باتری:</strong> {{ selectedLog.batteryLevel ? selectedLog.batteryLevel + '%' : '-' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>در شارژ:</strong> {{ selectedLog.batteryIsPlugged ? 'بله' : 'خیر' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>حافظه استفاده شده:</strong> {{ formatBytes(selectedLog.memoryUsed) }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>کل حافظه:</strong> {{ formatBytes(selectedLog.memoryTotal) }}</p>
            </b-col>
          </b-row>
        </b-card>

        <!-- اطلاعات اپلیکیشن -->
        <b-card class="mb-2" border-variant="primary">
          <h5 class="text-primary">
            <feather-icon icon="PackageIcon" />
            اطلاعات اپلیکیشن
          </h5>
          <b-row>
            <b-col md="3">
              <p><strong>نسخه:</strong> {{ selectedLog.appVersion || '-' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>بیلد:</strong> {{ selectedLog.appBuild || '-' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>پکیج:</strong> {{ selectedLog.appPackage || '-' }}</p>
            </b-col>
            <b-col md="3">
              <p><strong>Cordova:</strong> {{ selectedLog.cordovaVersion || '-' }}</p>
            </b-col>
          </b-row>
        </b-card>

        <!-- اطلاعات کاربر -->
        <b-card class="mb-2" border-variant="success">
          <h5 class="text-success">
            <feather-icon icon="UserIcon" />
            اطلاعات کاربر
          </h5>
          <b-row>
            <b-col md="4">
              <p><strong>نام:</strong> {{ selectedLog.userName || '-' }}</p>
            </b-col>
            <b-col md="4">
              <p><strong>موبایل:</strong> <span dir="ltr">{{ selectedLog.userMobile || '-' }}</span></p>
            </b-col>
            <b-col md="4">
              <p><strong>زمان محلی:</strong> {{ selectedLog.timezone || '-' }}</p>
            </b-col>
          </b-row>
        </b-card>

        <!-- لاگ‌های کنسول -->
        <b-card v-if="selectedLog.consoleLogs && selectedLog.consoleLogs.length" class="mb-2" border-variant="dark">
          <h5>
            <feather-icon icon="TerminalIcon" />
            لاگ‌های کنسول
          </h5>
          <pre class="bg-dark text-light p-2 small" style="max-height: 200px; overflow: auto;">{{ selectedLog.consoleLogs.join('\n') }}</pre>
        </b-card>

        <!-- یادداشت بررسی -->
        <b-card class="mb-2">
          <h5>
            <feather-icon icon="EditIcon" />
            یادداشت بررسی
          </h5>
          <b-form-textarea
            v-model="reviewNote"
            placeholder="یادداشت خود را اینجا بنویسید..."
            rows="3"
          />
          <b-button
            variant="success"
            class="mt-2"
            @click="saveReview"
          >
            <feather-icon icon="SaveIcon" />
            ذخیره و علامت بررسی شده
          </b-button>
        </b-card>

        <!-- زمان -->
        <p class="text-muted small">
          <strong>زمان وقوع خطا:</strong> {{ formatDateTime(selectedLog.errorTimestamp) }}
          <br>
          <strong>زمان ثبت:</strong> {{ formatDateTime(selectedLog.creationTime) }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BBadge,
  BAlert,
  BModal,
} from 'bootstrap-vue'
import crashLogService from '@/services/crashLog.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BBadge,
    BAlert,
    BModal,
  },
  data() {
    return {
      logs: [],
      stats: {},
      search: '',
      filterReviewed: '',
      filterVersion: '',
      appVersions: [],
      selectedLog: null,
      reviewNote: '',
      searchTimeout: null,
    }
  },
  computed: {
    headers() {
      return [
        { text: 'پیام خطا', value: 'errorMessage', sortable: false },
        { text: 'دستگاه', value: 'deviceInfo', sortable: false },
        { text: 'نسخه اپ', value: 'appVersion' },
        { text: 'کاربر', value: 'userInfo', sortable: false },
        { text: 'وضعیت', value: 'isReviewed' },
        { text: 'تاریخ', value: 'creationTime' },
        { text: 'عملیات', value: 'actions', sortable: false },
      ]
    },
  },
  created() {
    this.$vuetify.rtl = true
    this.loadData()
    this.loadStats()
  },
  methods: {
    async loadData() {
      try {
        const payload = {}
        if (this.filterReviewed !== '') {
          payload.isReviewed = this.filterReviewed
        }
        if (this.filterVersion) {
          payload.appVersion = this.filterVersion
        }
        if (this.search) {
          payload.search = this.search
        }

        const res = await crashLogService.getAll(payload)
        if (res.status === 'success') {
          this.logs = res.result.data
        }
      } catch (error) {
        console.error('Error loading crash logs:', error)
      }
    },
    async loadStats() {
      try {
        const res = await crashLogService.getStats()
        if (res.status === 'success') {
          this.stats = res.result.data
          this.appVersions = res.result.data.byAppVersion || []
        }
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    },
    onSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadData()
      }, 500)
    },
    showDetail(item) {
      this.selectedLog = item
      this.reviewNote = item.reviewNote || ''
      this.$bvModal.show('modal-detail')
    },
    async markAsReviewed(item) {
      try {
        const res = await crashLogService.review(item._id, true)
        if (res.status === 'success') {
          item.isReviewed = true
          this.loadStats()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: 'لاگ به عنوان بررسی شده علامت‌گذاری شد',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        console.error('Error marking as reviewed:', error)
      }
    },
    async saveReview() {
      try {
        const res = await crashLogService.review(
          this.selectedLog._id,
          true,
          this.reviewNote
        )
        if (res.status === 'success') {
          this.selectedLog.isReviewed = true
          this.selectedLog.reviewNote = this.reviewNote
          this.loadStats()
          this.$bvModal.hide('modal-detail')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: 'یادداشت ذخیره شد',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        console.error('Error saving review:', error)
      }
    },
    async deleteLog(item) {
      if (!confirm('آیا از حذف این لاگ مطمئن هستید؟')) return

      try {
        const res = await crashLogService.delete(item._id)
        if (res.status === 'success') {
          this.logs = this.logs.filter(l => l._id !== item._id)
          this.loadStats()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: 'لاگ حذف شد',
              icon: 'TrashIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        console.error('Error deleting log:', error)
      }
    },
    async confirmDeleteAll() {
      if (!confirm('آیا از حذف همه لاگ‌ها مطمئن هستید؟ این عملیات قابل بازگشت نیست!')) return

      try {
        const res = await crashLogService.deleteAll()
        if (res.status === 'success') {
          this.logs = []
          this.loadStats()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: `${res.result.deletedCount} لاگ حذف شد`,
              icon: 'TrashIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        console.error('Error deleting all logs:', error)
      }
    },
    truncate(str, length) {
      if (!str) return '-'
      return str.length > length ? str.substring(0, length) + '...' : str
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp * 1000).toLocaleDateString('fa-IR')
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('fa-IR') + ' - ' + date.toLocaleTimeString('fa-IR')
    },
    formatBytes(bytes) {
      if (!bytes) return '-'
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  text-decoration: underline;
}
pre {
  direction: ltr;
  text-align: left;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  border-radius: 4px;
}
</style>
